/**
 * newDealCloseDate.ts — the New Deal sheet's close-date rules (slice 3,
 * RULED 2026-10-06; spec §3.3 as corrected by ADOPTION.md §18). Pure: the
 * clock is a parameter, never read here, so every rule is tested on FIXED
 * dates (the spec's §3.6 dates are fixtures, not truth).
 *
 * Every date is a UTC calendar day as an ISO 'YYYY-MM-DD' string — the
 * expected_close wire's own shape, and the day the server compares against
 * ("overdue" is expected_close < today, UTC). Quarters are CALENDAR quarters
 * (Jan / Apr / Jul / Oct), the server's period rule (period.go).
 */

const DAY_MS = 86_400_000
const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** The month-end chip names the NEXT month once fewer than this many days
 *  are left in the current one. */
export const MONTH_ROLL_DAYS = 7
/** The default is the end of the NEXT quarter once fewer than this many
 *  days are left in the current one. */
export const QUARTER_ROLL_DAYS = 14

const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10)
const dayOf = (now: Date) => Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
// Day 0 of month m+1 is the last day of month m (Date.UTC normalises).
const monthEnd = (y: number, m: number) => Date.UTC(y, m + 1, 0)
const quarterEnd = (y: number, m: number) => monthEnd(y, Math.floor(m / 3) * 3 + 2)

/** Today as the UTC day. */
export function todayIso(now: Date): string {
  return iso(dayOf(now))
}

export interface ClosePick {
  key: 'month' | 'quarter' | 'in30' | 'in90'
  label: string
  iso: string
}

/** The four quick picks, in the spec's order. "End of month" becomes
 *  "End of {next month}" when fewer than MONTH_ROLL_DAYS days are left.
 *  "End of quarter" is the CURRENT quarter's end: the 14-day roll is the
 *  default's rule (defaultClose), not the chip's. */
export function closePicks(now: Date): ClosePick[] {
  const today = dayOf(now)
  const y = now.getUTCFullYear(), m = now.getUTCMonth()
  const thisMonthEnd = monthEnd(y, m)
  const roll = (thisMonthEnd - today) / DAY_MS < MONTH_ROLL_DAYS
  const month: ClosePick = roll
    ? { key: 'month', label: `End of ${MONTH_ABBR[(m + 1) % 12]}`, iso: iso(monthEnd(y, m + 1)) }
    : { key: 'month', label: 'End of month', iso: iso(thisMonthEnd) }
  return [
    month,
    { key: 'quarter', label: 'End of quarter', iso: iso(quarterEnd(y, m)) },
    { key: 'in30', label: 'In 30 days', iso: iso(today + 30 * DAY_MS) },
    { key: 'in90', label: 'In 90 days', iso: iso(today + 90 * DAY_MS) },
  ]
}

/** The default: the end of this quarter, or of the next one when fewer
 *  than QUARTER_ROLL_DAYS days are left. */
export function defaultClose(now: Date): string {
  const today = dayOf(now)
  const y = now.getUTCFullYear(), m = now.getUTCMonth()
  const end = quarterEnd(y, m)
  return (end - today) / DAY_MS < QUARTER_ROLL_DAYS ? iso(quarterEnd(y, m + 3)) : iso(end)
}

/** 'past' for a day before today (today itself has not passed — the
 *  server's own overdue rule); 'missing' for no date; null otherwise. */
export function closeWarning(closeIso: string, now: Date): 'past' | 'missing' | null {
  if (!closeIso) return 'missing'
  return closeIso < todayIso(now) ? 'past' : null
}

/** "Q4" — the calendar quarter of an ISO day. */
export function quarterLabel(closeIso: string): string {
  return `Q${Math.floor((Number(closeIso.slice(5, 7)) - 1) / 3) + 1}`
}
